import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../App.css';
import './PdfList.css';

const PdfList = () => {
  const [pdfs, setPdfs] = useState([]);
  const [submissions, setSubmissions] = useState([]); // State to store submission names
  const [selectedSubmission, setSelectedSubmission] = useState(''); // State for selected submission
  const [sortOrder, setSortOrder] = useState('desc'); // State to track sorting order
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [isDownloading, setIsDownloading] = useState(false); // State for download button
  const navigate = useNavigate(); // For navigation (redirecting)
  
  // Helper function to check if the token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    const payload = JSON.parse(atob(token.split('.')[1])); // Decode the token payload
    const exp = payload.exp; // Get expiration time
    const currentTime = Math.floor(Date.now() / 1000); // Get current time in seconds
    return exp < currentTime; // Return true if token is expired
  };

  // Function to refresh the token if it has expired
  const refreshAuthToken = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return false; // No refresh token available, require login
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_FQDN}/api/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.access_token); // Save the new access token
        return true;
      } else {
        console.error('Failed to refresh token');
        return false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  };

  // Fetch PDFs based on the selected submission
  const fetchPdfs = async () => {
    const token = localStorage.getItem('token');
    if (!token || isTokenExpired(token)) {
      const refreshed = await refreshAuthToken(); // Try to refresh the token
      if (!refreshed) {
        navigate('/login'); // Redirect to login if token is expired and refresh failed
        return;
      }
    }

    setIsLoading(true); // Start loading
    const url = selectedSubmission
      ? `${process.env.REACT_APP_FQDN}/api/pdfs?submission_name=${selectedSubmission}`
      : `${process.env.REACT_APP_FQDN}/api/pdfs`;
    const newToken = localStorage.getItem('token'); // Get the possibly refreshed token
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${newToken}`, // Include the token in the Authorization header
          'Content-Type': 'application/json',
        },
      });
      if (response.status === 401) {
        alert('Unauthorized. Please log in again.');
        navigate('/login'); // Redirect to login page
      } else {
        const data = await response.json();

        // Sort the PDFs by uploaded_date in descending order (most recent first)
        const sortedPdfs = data.pdfs.sort((a, b) => new Date(b.uploaded_date) - new Date(a.uploaded_date));
        setPdfs(sortedPdfs);
      }
    } catch (error) {
      console.error('Error fetching PDFs:', error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  // Function to download all results in CSV
  const downloadAllResults = async () => {
    const token = localStorage.getItem('token');
    if (!token || isTokenExpired(token)) {
      const refreshed = await refreshAuthToken();
      if (!refreshed) {
        navigate('/login');
        return;
      }
    }

    const newToken = localStorage.getItem('token');
    setIsDownloading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_FQDN}/api/download-all?submission_name=${selectedSubmission}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${newToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `results_${selectedSubmission}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        alert('Failed to download XLSX. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading XLSX:', error);
      alert('Error downloading XLSX. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    // Initial data fetch when component mounts and when selectedSubmission changes
    fetchPdfs();
  }, [selectedSubmission, navigate]); // Re-fetch PDFs when the selected submission changes

  useEffect(() => {
    // Fetch all submission names
    const fetchSubmissions = async () => {
      const token = localStorage.getItem('token'); // Get the possibly refreshed token
      if (!token || isTokenExpired(token)) {
        const refreshed = await refreshAuthToken(); // Try to refresh the token
        if (!refreshed) {
          navigate('/login'); // Redirect to login if token is expired and refresh failed
          return;
        }
      }

      const newToken = localStorage.getItem('token');
      try {
        const response = await fetch(`${process.env.REACT_APP_FQDN}/api/submissions`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${newToken}`, // Include the token in the Authorization header
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        setSubmissions(data.submissions || []); // Handle case where data.submissions is undefined or null
      } catch (error) {
        console.error('Error fetching submissions:', error);
      }
    };

    fetchSubmissions();
  }, [navigate]); // Re-fetch submissions when the token changes

  // Set up an interval to fetch PDFs every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchPdfs();
    }, 60000); // 60000 milliseconds = 60 seconds

    // Clear the interval when the component unmounts
    return () => clearInterval(interval);
  }, [selectedSubmission, navigate]);

  // Function to sort the PDFs based on uploaded_date
  const sortByDate = () => {
    const sortedPdfs = [...pdfs].sort((a, b) => {
      const dateA = new Date(a.uploaded_date);
      const dateB = new Date(b.uploaded_date);

      if (sortOrder === 'desc') {
        return dateA - dateB; // Ascending order
      } else {
        return dateB - dateA; // Descending order
      }
    });

    setPdfs(sortedPdfs);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); // Toggle sort order
  };

  return (
    <div className="containerpdf">
      <h2>Your Group's PDFs</h2>

      <div style={{ marginTop: '20px' }}>
        {submissions.length > 0 ? (
          <label htmlFor="submission-select" style={{ display: 'block', marginBottom: '10px' }}>
            Filter by Submission:
          </label>
        ) : (
          <p style={{ marginBottom: '10px' }}>No submissions available</p>
        )}

        {/* Dropdown and button in the same row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          {submissions.length > 0 && (
            <select
            className='submission-select'
              id="submission-select"
              value={selectedSubmission}
              onChange={(e) => setSelectedSubmission(e.target.value)}
              style={{ flex: 1, maxWidth: '50%' }}
            >
              <option value="">All Submissions</option>
              {submissions.map((submission) => (
                <option key={submission.id} value={submission.submission_name}>
                  {submission.submission_name}
                </option>
              ))}
            </select>
          )}

          {selectedSubmission && (
            <button
              onClick={downloadAllResults}
              disabled={isDownloading}
              style={{ padding: '10px 20px', width: "300px", marginBottom: "10px" }}
            >
              {isDownloading ? 'Downloading...' : 'Download All Results to XLSX'}
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <p>Loading PDFs...</p>
      ) : (
        <table className="pdf-table">
          <thead>
            <tr>
              <th className="pdflist-th submission-name-col">Submission Name</th>
              <th className="pdflist-th name-col">Name</th>
              <th className="pdflist-th uploaded-on-col">
                Uploaded On
                <span onClick={sortByDate} style={{ cursor: 'pointer', marginLeft: '8px' }}>
                  {sortOrder === 'asc' ? '⬆️' : '⬇️'}
                </span>
              </th>
              <th className="pdflist-th uploaded-by-col">Uploaded By</th>
              <th className="pdflist-th processed-col">Processed</th>
            </tr>
          </thead>
          <tbody>
            {pdfs.length > 0 ? (
              pdfs.map((pdf) => (
                <tr key={pdf.id}>
                  <td className="table-cell">
                    <div className="cell-content" title={pdf.submission_name}>
                      {pdf.submission_name}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="cell-content" title={pdf.name}>
                      <Link to={`/pdf/${pdf.id}`}>{pdf.name}</Link>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="cell-content" title={pdf.uploaded_date}>
                      {pdf.uploaded_date}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="cell-content" title={pdf.uploaded_by}>
                      {pdf.uploaded_by}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="cell-content" title={pdf.processed_status}>
                      {pdf.processed_status}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5">No PDFs available for this submission</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default PdfList;
